import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

import { orderLines_orderLines_edges_node } from "../../types/orderLines";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "16em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        tableLayout: "fixed"
      },
      tableRow: {
        cursor: "pointer"
      }
    };
  },
  { name: "orderLinesList" }
);

const formatter = new Intl.NumberFormat("en-PH", {
  currency: "PHP",
  currencyDisplay: "symbol",
  minimumFractionDigits: 2,
  style: "currency"
});

interface OrderLinesListProp extends ListProps {
  orderLines: orderLines_orderLines_edges_node[];
}

const OrderLinesList: React.FC<OrderLinesListProp> = ({
  orderLines,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  onRowClick,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 8;
  const classes = useStyles({});

  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Mall" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Brand" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="SKU Level Status" />
            </TableCellHeader>

            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Created Date" />
            </TableCellHeader>

            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Store Order Code" />
            </TableCellHeader>

            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Product" />
            </TableCellHeader>
            <TableCellHeader className={classes.colNarrow}>
              <FormattedMessage defaultMessage="Quantity" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Sales Amount" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {orderLines &&
            renderCollection(
              orderLines,
              (orderLine, index) => (
                <TableRow
                  className={!!orderLine ? classes.tableRow : undefined}
                  hover={!!orderLine}
                  key={orderLine ? index : "skeleton"}
                  onClick={
                    orderLine
                      ? onRowClick(btoa(`StoreOrder:${atob(orderLine.id)}`))
                      : undefined
                  }
                >
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => orderLine.mall_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => orderLine.brand_name,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => orderLine.sku_level_status,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    <Moment format="YYYY-MM-DD HH:mm:ss" tz="Asia/Manila">
                      {orderLine?.order_date ? (
                        orderLine.order_date
                      ) : (
                        <Skeleton />
                      )}
                    </Moment>
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => orderLine.store_order_code,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => orderLine.product_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => orderLine.quantity,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(() =>
                      formatter.format(maybe(() => orderLine.price_paid, 0))
                    )}
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No order lines found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default OrderLinesList;
